import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ApiRequestError } from '@/lib/api/client';
import { collectScreenAnchors, isKnownAnchor } from '@/lib/tour/anchors';
import { resolveAction, type ActionSpec } from '@/pages/aiagent/actions';
import { streamAgentChat, toHistory, type AgentEvent } from '@/pages/aiagent/agentChat';
import type { Citation } from '@/pages/aiagent/CitationCard';
import type { ChoicePayload } from '@/pages/aiagent/ChoiceCard';
import type { FloorplanPayload } from '@/pages/aiagent/FloorplanCard';
import type { IssuanceDraft } from '@/pages/aiagent/IssuancePreviewCard';
import type { MeetingCalendarPayload } from '@/pages/aiagent/MeetingCalendarCard';
import type { ProgressCurvePayload } from '@/pages/aiagent/ProgressCurveCard';
import type { DocDragPayload } from '@/pages/docs/dock/docDrag';
import type { ScreenTab } from '@/pages/aiagent/useScreenTab';
import {
  useAgentSessions,
  type AgentChatMessage, type AgentChatRating, type AgentChatSessionSummary, type AgentChatSessionDetail,
} from '@/pages/aiagent/sessions';
import type { ProbeDraft } from '@/pages/aiagent/probes';
import { isViewKind } from '@/pages/aiagent/views';
import { useIssuanceTemplates } from '@/pages/mypage/useIssuance';

/**
 * AI 대화의 <b>유일한 소유자</b>.
 *
 * <h2>왜 라우트 밖인가</h2>
 *
 * 예전에는 {@link AiAgentPage} 가 이 상태를 전부 쥐고 있었다. 라우트 컴포넌트라 화면을 옮기면
 * 언마운트되고 <b>대화가 통째로 날아간다</b> — 에이전트가 「현장 조회 열기」를 안내하고 그 링크를
 * 누른 사람이 방금 나눈 말을 잃는 구조였다. 안내할 수 있는 화면이 47개라, 에이전트가 유능해질수록
 * 이 이탈이 잦아진다.
 *
 * <p>{@link AppLayout} 안 · {@code Outlet} 밖에 두면 화면이 바뀌어도 죽지 않는다.
 * <b>답변을 받는 중에 화면을 옮겨도 SSE 가 끊기지 않는다</b> — 그것이 이 자리로 옮기는 이유다.
 *
 * <h2>대화는 한 벌이다</h2>
 *
 * 탭({@link AiAgentPage})과 도크({@link AiAgentDock})가 <b>같은 provider 를 본다.</b> 도크에서
 * 묻다가 탭을 열면 그대로 이어지고 반대도 된다. 두 벌로 두면 어느 쪽이 진짜인지 사용자가 판단해야 한다.
 *
 * <h2>안 쓰는 사람에게 비용을 지우지 않는다</h2>
 *
 * 세션 목록·서식 조회는 {@link AgentChatApi#activate} 가 불리기 전까지 돌지 않는다. 도크나 AI 탭이
 * 처음 열릴 때 켜진다 — 그냥 붙여 두면 AI 를 안 쓰는 사람의 모든 화면에서 요청이 두 건씩 나간다.
 */

export type ActionDraft = { spec: ActionSpec; payload: Record<string, unknown> };

/**
 * 물어 둔 쓰기에 대한 <b>짧은 대답</b>인가 (사용자 지시 2026-09-11).
 *
 * <h2>왜 목록인가</h2>
 *
 * 「네」를 모델에게 보내 판정시키면 한 턴이 더 돌고, 그 턴에서 모델이 도구 인자를 다시 채운다 —
 * 물었던 것과 다른 것이 저장될 수 있다. 승낙은 <b>이미 손에 있는 값</b>으로 실행해야 하므로
 * 판정도 여기서 한다.
 *
 * <h2>좁게 잡는다</h2>
 *
 * 말이 붙으면(「네, 근데 기한은 금요일로」) 그것은 승낙이 아니라 <b>고쳐 달라는 말</b>이다.
 * 통째로 맞아떨어질 때만 받고, 아니면 평소처럼 모델에게 보낸다 — <b>잘못 삼키는 쪽</b>이
 * 잘못 보내는 쪽보다 나쁘다.
 */
const YES = new Set(['네', '넵', '넹', '예', '응', '어', '그래', '그래요', '좋아', '좋아요',
  '확인', 'ㅇㅇ', 'ok', 'okay', 'yes', 'y', '해줘', '해주세요', '진행', '진행해줘', '실행']);

const NO = new Set(['아니', '아니요', '아니오', '아뇨', '취소', '하지마', '하지마요', '됐어',
  '됐어요', '괜찮아', '괜찮아요', 'ㄴㄴ', 'no', 'n']);

/**
 * 문장부호와 「, 」 뒤의 군더더기를 떼고 본다 — "네." · "네, 해주세요" 는 같은 대답이다.
 *
 * <p>물음표는 <b>떼지 않는다.</b> "네?" 는 되묻는 말이지 승낙이 아니다.
 */
const answerWord = (typed: string): string => {
  const squeezed = typed.toLowerCase().replace(/[.!~\s]/g, '');
  const head = /^(.+?),(?:네|응|그래|해주세요|해줘|부탁해요?|부탁드려요?)$/.exec(squeezed);
  return (head?.[1] ?? squeezed).replace(/,+$/, '');
};

const isPlainAnswer = (typed: string): boolean => {
  const word = answerWord(typed);
  return YES.has(word) || NO.has(word);
};

const isYes = (typed: string): boolean => YES.has(answerWord(typed));

export type AgentChatApi = {
  /** 세션 목록·서식 조회를 켠다. 여러 번 불러도 된다. */
  activate: () => void;

  // ── 대화 목록 ────────────────────────────────────────────────────────────
  sessions: AgentChatSessionSummary[];
  activeId: string | null;
  activeSession: AgentChatSessionDetail | undefined;
  messages: AgentChatMessage[];
  select: (id: string) => void;
  renameSession: (id: string, title: string) => void;
  deleteSession: (id: string) => void;

  // ── 이번 턴 ──────────────────────────────────────────────────────────────
  draft: string;
  setDraft: (value: string) => void;
  isReplying: boolean;
  /** 도착하는 중인 답변. 다 받으면 서버에 저장되고, 그때부터는 `messages` 가 그린다. */
  streaming: string;
  /**
   * 답이 오기 전 <b>지금 무엇을 하고 있는지</b> 한 줄(`step` 이벤트). 「앞선 요청 2건을 처리하고
   * 있습니다」·「대화가 길어 앞부분은 접고 답합니다」. 첫 글자가 오면 지운다 — 글자가 곧 진행이다.
   */
  progress: string | null;
  failure: string | null;
  sendMessage: (override?: string) => Promise<void>;
  startNew: () => void;
  /**
   * 쓰기가 실제로 실행된 뒤 부른다. 그 사실을 **대화 이력에 한 줄로** 남겨 다음 턴이 알게 한다.
   *
   * `role` 은 `assistant` 다 — 표에 `user | assistant` 둘뿐이라 이것이 스키마를 바꾸지 않는
   * 유일한 자리다. 사람이 승낙한 결과라는 것은 문장 앞의 `[실행됨]` 이 말한다.
   */
  recordReceipt: (receipt: string) => void;
  /**
   * 저장된 답변에 👍/👎. 열린 대화의 말에만 붙는다 — 흘러오는 중인 말풍선은 아직 id 가 없다.
   * 같은 것을 다시 누르면 되돌린다(`null`). 👎 는 서버가 갭 표에도 남긴다(관리자 대시보드).
   */
  rateMessage: (messageId: string, rating: AgentChatRating) => void;
  /**
   * <b>물어 둔 쓰기를 실행한다</b> — 「네」 (사용자 지시 2026-09-11).
   *
   * <p>버튼과 <b>손으로 적은 「네」</b>가 같은 자리로 들어온다({@link sendMessage} §승낙).
   * 실행 결과는 {@link recordReceipt} 가 이력에 남긴다.
   *
   * @param said 사용자가 승낙한 말. 이력에 <b>사용자 말풍선</b>으로 남는다 —
   *             버튼으로 눌렀으면 그 버튼의 문구({@link ActionSpec#confirmLabel})가 그 말이다
   */
  runAction: (said?: string) => Promise<void>;
  /** 물어 둔 쓰기를 버린다 — 「아니요」. 실행되는 것은 없고, 거절한 말만 이력에 남는다. */
  declineAction: (said?: string) => void;
  /** 실행이 나가 있는 동안. 버튼이 두 번 눌리는 것을 막는 값이다. */
  actionPending: boolean;
  /**
   * 자료실 도크에서 끌어다 놓은 <b>참조 문서</b>. 다음 발화의 첫 줄로 실려 간다.
   * (규칙과 한계는 {@link AgentChatProvider} 의 {@code pinnedDocs} 머리말에 있다.)
   */
  pinnedDocs: DocDragPayload[];
  pinDocument: (doc: DocDragPayload) => void;
  unpinDocument: (ref: string) => void;
  /**
   * 이 화면이 지금 다루고 있는 <b>현장 이름</b>을 알린다(`useScreenSite`).
   * 발화마다 함께 보내져 「이 현장」·「여기」 가 통하게 한다.
   */
  setScreenSite: (siteName: string | null) => void;
  /**
   * 이 화면이 지금 열고 있는 <b>탭의 권한 키</b>를 알린다(`useScreenTab`).
   * 「이거 수정하는 건 누구야」가 성립하게 하는 한 줄이다.
   */
  setScreenTab: (tab: ScreenTab | null) => void;

  /**
   * <b>지금 짚고 있는 화면의 자리</b> — `data-tour` 이름. 없으면 null (사용자 지시 2026-09-23).
   *
   * <p>그리는 것은 {@link AppLayout} 의 {@link SpotlightRing} 이다. 여기 두는 이유는 <b>대화가
   * 소유자</b>이기 때문이고, 도크·AI 탭 어느 쪽에서 물어도 같은 자리가 반짝여야 한다.
   */
  highlight: { target: string; label?: string } | null;
  /** 반짝임을 손으로 끈다. 짚어 준 것을 확인한 사람이 누른다. */
  clearHighlight: () => void;

  // ── 이번 턴에 딸려 온 카드 ───────────────────────────────────────────────
  issuanceDraft: IssuanceDraft | null;
  actionDraft: ActionDraft | null;
  links: { label: string; href: string; guide?: boolean }[];
  citations: Citation[];
  calendar: MeetingCalendarPayload | null;
  floorplan: FloorplanPayload | null;
  curve: ProgressCurvePayload | null;
  /** 이번 턴의 프로브 초안 — 「눌러서 조회해 보는」 칸 (DOC-109 §4). */
  probe: ProbeDraft | null;
  choice: ChoicePayload | null;
  /**
   * 슬롯 되물음의 보기(`ask` 이벤트). {@code choice} 와 같은 카드로 그리지만 나오는 자리가 달라
   * 따로 든다 — provider 의 {@code ask} 머리말에 그 이유가 있다.
   */
  ask: ChoicePayload | null;
};

const AgentChatContext = createContext<AgentChatApi | null>(null);

/**
 * 대화 상태. provider 밖에서 부르면 던진다 — 조용히 null 을 돌려주면 아무것도 안 뜬 이유를 못 찾는다.
 */
export function useAgentChat(): AgentChatApi {
  const api = useContext(AgentChatContext);
  if (!api) throw new Error('useAgentChat 은 <AgentChatProvider> 안에서만 쓸 수 있다');
  return api;
}

export function AgentChatProvider({ children }: { children: React.ReactNode }) {
  // 한 번 켜지면 다시 끄지 않는다. 껐다 켜면 캐시가 있는데도 목록이 한 번 빈 채로 깜빡인다.
  const [active, setActive] = useState(false);
  const activate = useCallback(() => setActive(true), []);

  // 쓰기가 끝난 뒤 낡은 화면을 내리는 데 쓴다({@link runAction}).
  const queryClient = useQueryClient();

  /**
   * 지금 보고 있는 화면. 매 발화에 실어 보낸다 — 「이 페이지 뭐야」가 성립하려면 필요하다.
   *
   * <b>ref 로 든다.</b> 의존성으로 걸면 화면을 옮길 때마다 `sendMessage` 가 새로 만들어지고,
   * 답변을 받는 중에 화면을 옮기면 그 턴이 <b>옛 함수와 새 함수로 갈린다.</b> 보내는 시점의
   * 값만 있으면 되는 것이라 ref 가 맞는 그릇이다.
   *
   * AI 탭(`/aiagent`) 자신은 보내지 않는다 — 「이 페이지」가 대화창 자신을 가리키게 되면
   * 사용자가 묻는 것과 어긋난다.
   */
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  /**
   * 에이전트에게 알릴 화면.
   *
   * <p>AI 대화창(`/aiagent`) 자신은 화면으로 세지 않는다 — 「이 페이지 뭐야」가 대화창을 가리키게
   * 되면 사용자가 묻는 것과 어긋난다. 대신 <b>직전에 보던 화면을 이어서 든다.</b>
   *
   * <p>처음에는 대화창에서 `null` 을 보냈다. 그런데 사용자가 마이페이지를 보다가 AI 탭을 열고
   * 「이 페이지 뭐야」라고 물으면, 그 사람에게 「지금 보고 있는 화면」은 <b>방금까지 보던
   * 마이페이지</b>다(사용자 지적 2026-09-01). 대화창은 화면을 덮은 것이지 화면을 떠난 것이 아니다.
   *
   * <p>도크에서는 애초에 본문이 그대로 있어 이 구분이 필요 없다 — 이 ref 는 <b>탭으로 들어간
   * 경우</b>를 위한 것이다.
   */
  const screenRef = useRef<string | null>(null);
  if (!pathname.startsWith('/aiagent')) {
    screenRef.current = pathname;
  }

  /**
   * 이 화면이 지금 열고 있는 현장 이름. 화면이 스스로 알린다(`useScreenSite`) —
   * 주소에는 id 만 있고 <b>이름은 화면이 이미 받아 그리고 있는 값</b>이라, 프로바이더가 다시
   * 조회할 이유가 없다.
   *
   * <b>id 가 아니라 이름을 보낸다.</b> 에이전트의 도구는 이름을 받아 스스로 id 로 바꾸고
   * (`tools/_site_ref.py`), 못 찾으면 후보를 되묻는다 — 모델도 화면도 UUID 를 다루지 않는다는
   * 그쪽 규율에 맞춘다. 인가는 어차피 backend 가 다시 판정한다.
   *
   * <p><b>어느 화면에서 알린 것인지 함께 든다.</b> 화면이 바뀔 때 여기서 지우는 방식은 쓸 수
   * 없다 — 자식(화면)의 effect 가 부모(여기)보다 먼저 돌아서, 새 화면이 방금 알린 값을 지운다.
   * 대신 <b>보낼 때 대조한다</b>: 알린 화면과 지금 화면이 다르면 그 값은 남의 것이다.
   */
  const siteRef = useRef<{ path: string; name: string } | null>(null);
  const setScreenSite = useCallback((siteName: string | null) => {
    siteRef.current = siteName ? { path: pathRef.current, name: siteName } : null;
  }, []);
  const currentSite = () =>
    siteRef.current && siteRef.current.path === pathRef.current ? siteRef.current.name : null;

  /**
   * 이 화면이 지금 열고 있는 <b>탭의 권한 키</b>(`useScreenTab`). 「이거 수정하는 건 누구야」가
   * 성립하려면 필요하다 — 탭은 주소의 `?tab=` 에 있어 위 {@code screenRef}(경로)에 실리지 않는다.
   *
   * <p>현장 이름과 <b>같은 모양</b>으로 든다: 알린 화면을 함께 담고 보낼 때 대조한다. 화면이
   * 바뀔 때 여기서 지우면 자식의 effect 가 먼저 돌아 새 화면의 값을 지운다(위 §siteRef).
   */
  const tabRef = useRef<{ path: string; tab: ScreenTab } | null>(null);
  const setScreenTab = useCallback((tab: ScreenTab | null) => {
    tabRef.current = tab ? { path: pathRef.current, tab } : null;
  }, []);
  const currentTab = () =>
    tabRef.current && tabRef.current.path === pathRef.current ? tabRef.current.tab : null;

  const {
    sessions, activeId, activeSession, select, clearActive, createSession, appendMessage, renameSession, deleteSession,
    rateMessage: rateInSession,
  } = useAgentSessions(active);

  // 초안 카드가 보여 줄 서식 이름. 모델이 부르는 이름을 그대로 쓰면 인사팀이 서식명을 바꿨을 때
  // 대화만 옛 이름을 말한다.
  const { data: templates } = useIssuanceTemplates(active);

  const messages = useMemo(() => activeSession?.messages ?? [], [activeSession]);

  const [draft, setDraft] = useState('');

  /**
   * <b>자료실 도크에서 끌어다 놓은 문서</b>(사용자 지시 2026-09-04 · {@code docDrag}).
   *
   * <h2>이름은 말풍선에, <b>키는 요청에</b></h2>
   * 발화 첫 줄의 «[참조 문서] 이름» 은 <b>사람이 읽는 줄</b>이다 — 무엇을 근거로 물었는지가
   * 이력에 남아야 나중에 답을 다시 읽을 수 있다. 그와 <b>별도로</b> 저장소 키를 요청에 싣는다
   * ({@code docs}).
   *
   * <p>처음에는 이름만 보냈다. 그런데 회사 파일 이름은 부서·사람·숫자를 그대로 담고 있어
   * (`20260903_공무팀_80-20_500-350-4_유영진.png`) 모델이 그 안의 「공무팀」을 <b>현장 이름</b>으로
   * 읽었고, 「이 문서 뭐야」에 「'공무팀' 현장을 찾지 못했습니다」가 돌아왔다(실측 2026-09-04).
   * 이름은 가리키는 말로 쓰기에 너무 애매하다.
   *
   * <p>키가 있으면 에이전트는 이름을 해석할 일이 없다 — 그 파일이 <b>이미 분석돼 있는지</b>를
   * 묻고, 아니면 <b>그 자리에서</b> 추출·OCR·임베딩을 돌린 뒤 답을 잇는다
   * ({@code tools/docs.ask_attached_document}).
   *
   * <h2>보낸 뒤에도 <b>붙어 있다</b> (사용자 지시 2026-09-04)</h2>
   * 처음에는 보내고 나면 비웠다. 「언제 잊을 것인가」를 만들지 않으려던 것인데, 그 대신
   * <b>대화가 한 턴 만에 끊겼다</b> — 「이 문서 뭐야」 로 답을 받고 「그럼 계약금액은?」 하고
   * 물으면 그 턴에는 붙인 파일이 없다. 사람은 같은 문서를 다시 끌어다 놓아야 했다.
   *
   * <p>그래서 <b>떼기 전까지 붙여 둔다.</b> 「언제 잊을 것인가」의 답은 <b>사람이 보고 정한다</b> —
   * 칩이 입력칸 위에 그대로 서 있고(`AgentChatView`) 옆의 ✕ 로 뗀다. 새 대화를 시작하면 함께
   * 비워진다. 잘못 남은 참조가 조용히 틀린 답이 되는 것을 막는 것은 <b>보이는 것</b>이지 비우는
   * 것이 아니었다.
   */
  const [pinnedDocs, setPinnedDocs] = useState<DocDragPayload[]>([]);

  const pinDocument = useCallback((doc: DocDragPayload) => {
    // 같은 파일을 두 번 끌어다 놓아도 한 줄이다.
    setPinnedDocs((prev) => (prev.some((row) => row.ref === doc.ref) ? prev : [...prev, doc]));
  }, []);

  const unpinDocument = useCallback((ref: string) => {
    setPinnedDocs((prev) => prev.filter((row) => row.ref !== ref));
  }, []);

  const [isReplying, setIsReplying] = useState(false);
  const [streaming, setStreaming] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * 이번 턴의 발급 초안. 에이전트가 `action` 이벤트로 내려 준다.
   *
   * 하나만 들고 있는다 — 대화 끝에 붙는 "지금 확인할 것" 이라 여러 개가 쌓이면 어느 것을 눌러야
   * 하는지 알 수 없다. 새 발화를 보내면 지운다.
   */
  const [issuanceDraft, setIssuanceDraft] = useState<IssuanceDraft | null>(null);
  /**
   * 이번 턴의 일반 쓰기 초안 — <b>에이전트가 묻고 답을 기다리는 것</b>. 발급처럼 전용 카드가
   * 없는 kind 는 여기로 온다.
   *
   * <p><b>ref 를 함께 든다.</b> {@link sendMessage} 가 손으로 적은 「네」를 이 초안으로 받아야
   * 하는데, 의존성으로 걸면 초안이 도착할 때마다 `sendMessage` 가 새로 만들어진다 — 초안은
   * 답변을 받는 <b>도중에</b> 도착하므로 그 턴이 옛 함수와 새 함수로 갈린다(위 §screenRef 와 같은 자).
   */
  const [actionDraft, setActionDraft] = useState<ActionDraft | null>(null);
  const actionDraftRef = useRef<ActionDraft | null>(null);
  const putActionDraft = useCallback((next: ActionDraft | null) => {
    actionDraftRef.current = next;
    setActionDraft(next);
  }, []);
  /** 실행이 나가 있는 동안. 같은 초안이 두 번 실행되는 것을 막는다. */
  const [actionPending, setActionPending] = useState(false);
  const actionPendingRef = useRef(false);
  /**
   * 에이전트가 넘겨준 화면. `guide` 가 붙은 버튼은 옮기고 끝나지 않는다 — 그 화면의 사용 안내까지
   * 편다(DOC-20 §1.18).
   */
  const [links, setLinks] = useState<{ label: string; href: string; guide?: boolean }[]>([]);
  /**
   * 이번 턴의 근거 문서. 초안 카드와 같은 규칙으로 이번 턴 것만 들고 있는다 — 지난 답변의 근거가
   * 남아 있으면 사용자가 <b>방금 답의 근거로 착각한다.</b>
   */
  const [citations, setCitations] = useState<Citation[]>([]);
  const [calendar, setCalendar] = useState<MeetingCalendarPayload | null>(null);
  const [floorplan, setFloorplan] = useState<FloorplanPayload | null>(null);
  const [curve, setCurve] = useState<ProgressCurvePayload | null>(null);
  /**
   * 이번 턴의 프로브 초안(DOC-109 §4). 다른 카드와 같은 규칙으로 <b>이번 턴 것만</b> 들고 있다 —
   * 지난 턴의 조회 칸이 남아 있으면 방금 물음의 답으로 착각한다.
   */
  const [probe, setProbe] = useState<ProbeDraft | null>(null);
  /** 이번 턴의 되물음 선택지. 후보가 여럿이라 에이전트가 답하지 않고 물은 것이다(DOC-20 §1.9). */
  const [choice, setChoice] = useState<ChoicePayload | null>(null);
  /**
   * 이번 턴의 <b>슬롯 되물음</b> 선택지 — `ask` 이벤트 (2026-09-23 · DOC-94 §3 G).
   *
   * <h2>왜 {@code choice} 와 따로 드는가</h2>
   * 그리는 카드는 같지만({@link ChoiceCard}) 나오는 자리가 다르다 — {@code choice} 는 도구가
   * 후보를 여럿 찾았을 때고, 이쪽은 발급 서식의 <b>빈 칸</b>을 채우려 묻는 것이다
   * (`issuer.py` §③). 한 칸에 담으면 둘이 같은 턴에 오는 날 하나가 말없이 사라진다.
   *
   * <h2>물음은 담지 않는다</h2>
   * `ask.label` 은 <b>답변 말풍선과 같은 문자열</b>이다(`issuer.py` 가 그 label 로 `_reply` 한다).
   * 그래서 카드에는 버튼만 놓는다({@link ChoicePayload#question}). `options` 가 비어 오는 슬롯
   * (제출처)도 있는데, 그때는 카드가 스스로 아무것도 안 그린다 — 지어낸 보기를 채우지 않는다.
   */
  const [ask, setAsk] = useState<ChoicePayload | null>(null);

  /**
   * <b>지금 짚고 있는 자리</b>(사용자 지시 2026-09-23).
   *
   * <h2>다음 발화에서 지우지 않는다</h2>
   * 다른 턴 것들({@code links}·{@code citations})은 {@link clearTurn} 이 걷는다. 반짝임은 다르다 —
   * 짚어 준 목적이 <b>사용자가 그것을 누르는 것</b>이라, 누르기 전에 지우면 짚어 준 일이 없던 일이
   * 된다. 사람이 그 버튼을 누르러 가는 동안 대화창에 손을 대지 않는다는 법도 없다.
   *
   * <p>지워지는 자리는 셋이다: ① 그 자리가 화면에서 사라질 때(SpotlightRing 이 알아서 안 그린다)
   * ② 다른 자리를 새로 짚을 때 ③ 사람이 껐을 때({@link clearHighlight}).
   */
  const [highlight, setHighlight] = useState<{ target: string; label?: string } | null>(null);
  const clearHighlight = useCallback(() => setHighlight(null), []);

  /**
   * 이번 발화와 함께 보낸 자리 목록. 돌아온 이름을 <b>대조하는 데만</b> 쓴다 —
   * 보낸 적 없는 이름을 짚으면 허공이 반짝인다(`anchors.ts` §isKnownAnchor).
   *
   * <p>ref 인 이유는 {@code screenRef} 와 같다 — 의존성으로 걸면 화면을 옮길 때마다
   * {@code sendMessage} 가 새로 만들어져 답변 받는 도중의 턴이 옛 함수와 새 함수로 갈린다.
   */
  const anchorsRef = useRef<{ name: string; label: string }[]>([]);

  /**
   * 화면이 바뀌면 <b>짚어 둔 것을 끈다.</b> 이름({@code data-tour})은 화면마다 겹칠 수 있어
   * ({@code page-tabs}·{@code page-actions} 는 거의 모든 화면에 있다), 남겨 두면 <b>엉뚱한 화면의
   * 같은 이름</b>이 반짝인다 — 짚어 준 적 없는 자리를 가리키는 셈이다.
   */
  useEffect(() => {
    setHighlight(null);
  }, [pathname]);

  /** 이번 턴에 딸려 온 것들을 걷는다. 새 발화·대화 전환에서 함께 부른다. */
  const clearTurn = useCallback(() => {
    setStreaming('');
    setProgress(null);
    setFailure(null);
    setIssuanceDraft(null);
    putActionDraft(null);
    setLinks([]);
    setCitations([]);
    setCalendar(null);
    setFloorplan(null);
    setCurve(null);
    setProbe(null);
    setChoice(null);
    setAsk(null);
  }, [putActionDraft]);

  // 대화를 바꾸면 저장 못 한 채 남아 있던 말풍선을 버린다 — 다른 대화에 끼어들면 안 된다.
  useEffect(() => {
    clearTurn();
  }, [activeId, clearTurn]);

  const startNew = useCallback(() => {
    clearActive();
    setDraft('');
    // 끌어다 놓은 참조도 함께 버린다 — 새 대화는 앞 대화의 문서를 물려받지 않는다(도크 머리말).
    setPinnedDocs([]);
    clearTurn();
  }, [clearActive, clearTurn]);

  /**
   * 쓰기가 실행된 뒤 그 사실을 대화 이력에 남긴다.
   *
   * <p>열린 대화가 없으면 남길 곳도 없다 — 초안은 대화에서 나왔으므로 실제로는 늘 있다.
   * <p>저장에 실패해도 <b>조용히 넘긴다.</b> 실행은 이미 끝났고, 여기서 오류를 띄우면 사용자는
   * 실행이 실패한 줄 안다. 잃는 것은 "다음 턴이 이 사실을 안다" 뿐이다.
   */
  const recordReceipt = useCallback(
    (receipt: string) => {
      if (!activeId || !receipt) return;
      void appendMessage(activeId, { role: 'assistant', content: receipt }).catch(() => {});
    },
    [activeId, appendMessage],
  );

  /**
   * <b>「네」 — 물어 둔 쓰기를 실행한다</b> (사용자 지시 2026-09-11).
   *
   * <h2>왜 여기인가</h2>
   *
   * 예전에는 확인 카드가 자기 안에서 실행했다. 이제 승낙이 <b>두 곳</b>에서 온다 — 버튼과
   * 손으로 적은 「네」({@link sendMessage}) — 그래서 실행하는 코드가 둘 다 닿는 자리에 있어야 한다.
   * 두 벌로 두면 한쪽만 캐시를 내리거나 한쪽만 영수증을 남기는 날이 온다.
   *
   * <h2>계약은 그대로다</h2>
   *
   * 부르는 것은 <b>프론트가 사용자 세션</b>으로 한다({@code spec.submit} → `apiFetch`). 위임
   * 토큰은 GET 전용이라 애초에 쓰기가 막혀 있고, 그래야 감사 로그의 "누가" 가 사람으로 남는다.
   */
  const runAction = useCallback(async (said?: string) => {
    const draftAction = actionDraftRef.current;
    // 실행이 나가 있는 동안에는 두 번째를 받지 않는다. 조회라면 한 번 더 부르는 것으로 끝나지만
    // **여기서 두 번은 두 건이 저장된다.**
    if (!draftAction?.spec.submit || actionPendingRef.current) return;

    setFailure(null);
    actionPendingRef.current = true;
    setActionPending(true);
    // 승낙은 **사람이 한 말**이라 사용자 말풍선으로 남는다. 다음 턴의 모델도 이것을 읽는다 —
    // 「아까 그거 등록했어?」에 답하려면 누가 무엇을 승낙했는지가 이력에 있어야 한다.
    if (activeId) {
      await appendMessage(activeId, { role: 'user', content: said ?? draftAction.spec.confirmLabel })
        .catch(() => {});
    }
    try {
      const result = await draftAction.spec.submit(draftAction.payload);
      // 물음은 끝났다. 버튼이 남아 있으면 같은 초안이 다시 실행된다.
      putActionDraft(null);
      // 이 쓰기가 바꾼 화면은 대화 밖에 있다. 무엇이 낡았는지는 카탈로그가 안다.
      draftAction.spec.invalidates?.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
      // 실행은 이미 끝났다. 이력에 남기는 데 실패해도 **되돌리지 않는다**.
      recordReceipt(result.receipt ?? result.message);
      // "주간업무 열기" 같은 뒷일. 에이전트가 준 링크와 같은 줄에 선다.
      if (result.links?.length) setLinks((prev) => [...prev, ...result.links!]);
    } catch (e) {
      // 서버가 준 사유를 그대로 보여 준다. 권한 거절("...권한이 없습니다")을 일반 오류로 뭉개면
      // 사용자는 될 일을 안 되는 줄 알고 다시 시킨다. **초안은 남겨 둔다** — 고치고 다시 누른다.
      setFailure(e instanceof Error ? e.message : '요청을 처리하지 못했습니다.');
    } finally {
      actionPendingRef.current = false;
      setActionPending(false);
    }
  }, [activeId, appendMessage, putActionDraft, queryClient, recordReceipt]);

  /**
   * 「아니요」. 물음을 걷고 아무것도 실행하지 않는다.
   *
   * <p>거절도 <b>이력에 남긴다.</b> 대답 없이 물음만 사라지면 다음 턴의 모델은 그 초안이
   * 실행됐는지 거절됐는지 모른 채 「방금 그거 어떻게 됐어」에 답해야 한다.
   */
  const declineAction = useCallback((said?: string) => {
    if (actionPendingRef.current || !actionDraftRef.current) return;
    putActionDraft(null);
    if (!activeId) return;
    void (async () => {
      await appendMessage(activeId, { role: 'user', content: said ?? '아니요' });
      await appendMessage(activeId, { role: 'assistant', content: '알겠습니다. 진행하지 않았습니다.' });
    })().catch(() => {});
  }, [activeId, appendMessage, putActionDraft]);

  const sendMessage = useCallback(
    async (override?: string) => {
      const typed = (override ?? draft).trim();
      if (!typed || isReplying) return;

      /*
        ## 승낙 — **손으로 적은 「네」도 버튼과 같은 자리로 들어온다** (사용자 지시 2026-09-11)

        에이전트가 물어 둔 쓰기가 있을 때 "네" 라고 적으면, 그 말을 모델에게 보내지 않고 **여기서
        실행한다.** 보내면 모델이 같은 도구를 다시 부르는데, 그때 인자를 한 칸 흘리면
        **물었던 것과 다른 것이 저장된다** — 실행할 값은 이미 손에 있다.

        짧은 대답만 받는다(`네`·`아니요`). "네, 근데 기한은 금요일로" 처럼 말이 붙으면 그것은
        승낙이 아니라 **고쳐 달라는 말**이라 모델에게 가야 한다.
      */
      if (actionDraftRef.current && !actionPendingRef.current && isPlainAnswer(typed)) {
        // 적은 말을 그대로 넘긴다 — 이력에 남는 승낙은 **사용자가 실제로 한 말**이어야 한다.
        setDraft('');
        if (isYes(typed)) await runAction(typed);
        else declineAction(typed);
        return;
      }

      // 끌어다 놓은 문서는 <b>발화의 첫 줄</b>이 된다(§pinnedDocs). 말풍선에도 그대로 남는다 —
      // 「무엇을 근거로 물었는가」가 이력에 있어야 나중에 답을 다시 읽을 수 있다.
      const text = pinnedDocs.length
        ? `${pinnedDocs.map((doc) => `[참조 문서] ${doc.name}`).join('\n')}\n${typed}`
        : typed;

      // 요청에 함께 실을 <b>저장소 키</b>. 떼기 전까지 계속 실린다(§pinnedDocs).
      const attachedDocs = pinnedDocs.map((doc) => ({
        // NAS 는 연결 id 가 있어야 에이전트가 docRef(`NAS:{연결}:{경로}`)를 세운다(`tools/docs._doc_ref`).
        source: doc.source, ref: doc.ref, name: doc.name, connectionId: doc.connectionId,
      }));

      const history = toHistory(messages);

      setDraft('');
      // ★ 붙인 파일은 **비우지 않는다.** 비우면 「그럼 계약금액은?」 같은 후속 물음에서
      //   그 문서가 사라져 대화가 한 턴 만에 끊긴다. 떼는 것은 사람이 칩의 ✕ 로 한다.
      clearTurn();
      setIsReplying(true);

      let answer = '';
      let failed: string | null = null;
      // 어느 단계에서 깨졌는지. 이게 없으면 무슨 일이 나든 "대화를 저장하지 못했습니다" 한 줄로
      // 뭉개져, 정작 원인(세션 만료·스트림 끊김)을 짚을 수 없다.
      let stage: '저장' | '응답' = '저장';
      let saved = false;

      try {
        // 열린 대화가 없으면 서버에 새로 만든다.
        const sessionId = activeId ?? (await createSession());
        await appendMessage(sessionId, { role: 'user', content: text });

        stage = '응답';
        /*
          ★ 지금 화면에 떠 있는 자리들을 <b>보내기 직전에</b> 걷는다(`anchors.ts`).

          미리 들고 있을 수 없다 — 사람이 탭을 옮기고 창을 열고 접는 사이에 목록이 통째로 바뀐다.
          그리고 걷는 비용은 `querySelectorAll` 한 번이라, 발화마다 새로 세는 편이 싸고 정확하다.
        */
        anchorsRef.current = collectScreenAnchors(pathRef.current);

        await streamAgentChat(
          { message: text, sessionId, history, screen: screenRef.current, site: currentSite(),
            tab: currentTab(), docs: attachedDocs, anchors: anchorsRef.current },
          (event: AgentEvent) => {
          if (event.type === 'token') {
            // `replace` 는 완성문으로 갈아 끼우는 마지막 조각이다(`AgentEvent` 주석).
            answer = event.replace ? event.text : answer + event.text;
            setStreaming(answer);
            setProgress(null);
          }
          else if (event.type === 'step') {
            // 답이 오기 전의 진행 상황. 글자가 오기 시작하면 위에서 지운다.
            setProgress(event.label);
          }
          else if (event.type === 'action') {
            // 초안이지 실행이 아니다. 에이전트는 값을 말하고 물을 뿐, 승낙하는 것은 사람이다(§5.4).
            //
            // 어떤 kind 를 받을 수 있는지는 `actions.ts` 카탈로그가 안다 — 여기에 분기를 늘리지
            // 않는다. **모르는 kind 는 조용히 넘기지 않는다**: 에이전트는 초안을 내렸는데 화면에
            // 아무것도 없으면 사용자는 자기 요청이 사라진 줄 안다.
            const payload = (event.payload ?? {}) as Record<string, unknown>;
            const spec = resolveAction(event.kind);

            if (!spec) {
              failed = `처리할 수 없는 요청입니다 (${event.kind}). 화면에서 직접 진행해 주세요.`;
            }
            else if (spec.kind === 'submit_issuance') {
              setIssuanceDraft({
                templateKey: String(payload.templateKey ?? ''),
                templateName: templates?.find((t) => t.templateKey === payload.templateKey)?.name,
                purpose: (payload.purpose as string) ?? null,
                recipient: (payload.recipient as string) ?? null,
                copies: (payload.copies as number) ?? 1,
              });
            }
            else {
              putActionDraft({ spec, payload });
            }
          }
          else if (event.type === 'view') {
            // 모르는 kind 는 **조용히 넘긴다.** 초안(`action`)이 사라지면 사용자의 요청이 증발하지만,
            // 조회 카드는 같은 내용이 답변 문장에 이미 있다 — 그림이 하나 없을 뿐이다.
            if (isViewKind(event.kind) && event.kind === 'meeting_calendar') {
              setCalendar(event.payload as unknown as MeetingCalendarPayload);
            }
            else if (isViewKind(event.kind) && event.kind === 'choice') {
              setChoice(event.payload as unknown as ChoicePayload);
            }
            else if (isViewKind(event.kind) && event.kind === 'floorplan') {
              setFloorplan(event.payload as unknown as FloorplanPayload);
            }
            else if (isViewKind(event.kind) && event.kind === 'progress_curve') {
              setCurve(event.payload as unknown as ProgressCurvePayload);
            }
          }
          else if (event.type === 'probe') {
            // 키만 받는다 — 경로는 서버 카탈로그가 안다. 모르는 키면 카드가 「열려 있지 않습니다」
            // 라고 말한다(조용히 넘기지 않는다 — 요청이 증발한 것처럼 보이면 안 된다).
            setProbe({ key: event.key, values: event.values ?? {} });
          }
          else if (event.type === 'link') {
            setLinks((prev) => [...prev, { label: event.label, href: event.href, guide: event.guide }]);
          }
          else if (event.type === 'highlight') {
            // ★ **우리가 보낸 목록에 있는 이름만** 짚는다. 없는 화면을 고른 `link` 를 버리는 것과
            //   같은 자리다 — 허공이 반짝이면 사용자는 자기가 뭘 놓쳤나 하고 화면을 훑는다.
            if (isKnownAnchor(event.target, anchorsRef.current)) {
              setHighlight({ target: event.target, label: event.label });
            }
          }
          else if (event.type === 'citation') {
            // 근거 문서. **링크가 아니라 id 만 온다** — 원본을 여는 링크는 사용자가 카드를 누른
            // 그 순간 백엔드가 만든다(`CitationCard`).
            setCitations((prev) => {
              const key = `${event.documentFileId}:${event.clauseRef ?? ''}`;
              return prev.some((c) => `${c.documentFileId}:${c.clauseRef ?? ''}` === key)
                ? prev
                : [...prev, { documentFileId: event.documentFileId, name: event.name, clauseRef: event.clauseRef }];
            });
          }
          else if (event.type === 'preview') {
            /*
              발급 미리보기 (2026-09-23 · DOC-94 §3 G).

              ## 카드가 두 번 뜨지 않는 이유

              `issuer.py` §④ 는 `preview` 를 내고 **곧바로** `action:submit_issuance` 를 낸다 —
              같은 발급 한 건이다. 그런데 이 카드가 앉는 자리는 `issuanceDraft` **한 칸**이라,
              뒤엣것이 앞엣것을 덮을 뿐 카드가 둘이 되지 않는다.

              ## 그런데 왜 붙이는가

              여태 이 이벤트를 버렸던 탓에 미리보기가 **쓰기 초안 카탈로그에 매달려 있었다** —
              `actions.ts` 에서 `submit_issuance` 한 줄이 빠지거나 이름이 바뀌면 "처리할 수 없는
              요청입니다" 만 뜨고 미리보기는 영영 안 뜬다. 미리보기는 조회지 쓰기가 아니다.
              그래서 보는 것은 `preview` 가, 신청 버튼의 값은 `action` 이 댄다.

              `fields` 는 `issuer` 의 슬롯 그대로다(`language`·`period` 도 들어 있다). 카드가
              쓰는 네 칸만 꺼낸다 — 나머지는 서식 본문을 굽는 backend 가 다시 본다.
            */
            if (event.kind === 'issuance') {
              const fields = event.fields ?? {};
              setIssuanceDraft({
                templateKey: event.form,
                templateName: templates?.find((t) => t.templateKey === event.form)?.name,
                purpose: (fields.purpose as string) ?? null,
                recipient: (fields.recipient as string) ?? null,
                copies: (fields.copies as number) ?? 1,
              });
            }
          }
          else if (event.type === 'ask') {
            // 슬롯 되물음. 누르면 그 말이 **새 발화로** 나가고 `issuer` 가 그 턴을 이어받는다
            // (`resuming` — 짧은 답은 발화 자체를 슬롯 값으로 받는다). 물음은 이미 말풍선에
            // 있으므로 담지 않는다(§ask 머리말).
            setAsk({
              options: (event.options ?? []).map((option) => ({
                label: option, utterance: option, hint: null,
              })),
            });
          }
          else if (event.type === 'error') {
            failed = event.message;
          }
          },
        );

        // 받은 만큼만 저장한다. 스트림이 도중에 끊겨도 거기까지는 남는다.
        stage = '저장';
        if (answer) {
          await appendMessage(sessionId, { role: 'assistant', content: answer });
          saved = true;
        }
        setFailure(failed ?? (answer ? null : '답변을 받지 못했습니다.'));
      } catch (error) {
        // 서버가 준 사유를 그대로 보여 준다. "다른 기기에서 로그인하여 이 세션이 종료되었습니다"
        // 같은 메시지가 여기서 지워지면, 사용자는 로그인 문제를 저장 문제로 오해한다.
        const detail = error instanceof ApiRequestError ? error.message : null;
        setFailure(detail ?? `${stage} 단계에서 오류가 발생했습니다.`);
      } finally {
        // 저장에 성공했으면 목록이 그린다 → 임시 말풍선을 지운다.
        // 실패했으면 **지우지 않는다.** 받아 놓은 말을 오류 문구 하나 때문에 화면에서 없애면,
        // 사용자는 답을 봤다가 빼앗긴다.
        if (saved || !answer) setStreaming('');
        setProgress(null);
        setIsReplying(false);
      }
    },
    [draft, isReplying, messages, activeId, createSession, appendMessage, templates, clearTurn,
     pinnedDocs, putActionDraft, runAction, declineAction],
  );

  // 평가는 열린 대화의 말에만 붙는다. 대화 id 는 화면이 아니라 여기가 안다.
  const rateMessage = useCallback(
    (messageId: string, rating: AgentChatRating) => {
      if (!activeId) return;
      rateInSession(activeId, messageId, rating);
    },
    [activeId, rateInSession],
  );

  const api = useMemo<AgentChatApi>(
    () => ({
      activate,
      sessions, activeId, activeSession, messages, select, renameSession, deleteSession,
      draft, setDraft, isReplying, streaming, progress, failure, sendMessage, startNew,
      recordReceipt, rateMessage, setScreenSite, setScreenTab,
      runAction, declineAction, actionPending,
      pinnedDocs, pinDocument, unpinDocument,
      highlight, clearHighlight,
      issuanceDraft, actionDraft, links, citations, calendar, floorplan, curve, probe, choice, ask,
    }),
    [
      activate, sessions, activeId, activeSession, messages, select, renameSession, deleteSession,
      draft, isReplying, streaming, progress, failure, sendMessage, startNew,
      recordReceipt, rateMessage, setScreenSite, setScreenTab,
      runAction, declineAction, actionPending,
      pinnedDocs, pinDocument, unpinDocument,
      highlight, clearHighlight,
      issuanceDraft, actionDraft, links, citations, calendar, floorplan, curve, probe, choice, ask,
    ],
  );

  return <AgentChatContext.Provider value={api}>{children}</AgentChatContext.Provider>;
}
